import React, { useCallback, useEffect, useState } from 'react';
import { ref, update, get } from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { db, storage } from '../../../config/firebase';

const DocumentUpload = ({
  formData = { photo: null, certificates: [] },
  updateFormData,
  isEditing,
  studentId,
}) => {
  const [photoPreview, setPhotoPreview] = useState(null);
  const [labels, setLabels] = useState({});
  const [documents, setDocuments] = useState([]);

  const loadExistingDocuments = useCallback(async () => {
    try {
      const snapshot = await get(
        ref(db, `students/${studentId}/documents/certificates`),
      );
      if (snapshot.exists()) {
        const existingDocs = snapshot.val();
        // Convert object to array if needed
        const docsArray = Object.values(existingDocs);
        setDocuments(docsArray);

        // Set initial labels
        const initialLabels = {};
        docsArray.forEach((doc, index) => {
          initialLabels[index] = doc.label || '';
        });
        setLabels(initialLabels);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  }, [studentId]);

  useEffect(() => {
    if (formData?.photo) {
      if (typeof formData.photo === 'string') {
        setPhotoPreview(formData.photo);
      } else if (formData.photo instanceof File) {
        setPhotoPreview(URL.createObjectURL(formData.photo));
      }
    }

    if (isEditing) {
      loadExistingDocuments();
    }
  }, [formData?.photo, isEditing, loadExistingDocuments]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateFormData({
        ...formData,
        photo: file,
      });
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCertificateUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => file.size <= 10 * 1024 * 1024);

    if (validFiles.length !== files.length) {
      alert(
        'Some files were not added because they exceed the 10MB size limit.',
      );
    }

    updateFormData({
      ...formData,
      certificates: [...(formData.certificates || []), ...validFiles],
    });
  };

  const removeCertificate = (index) => {
    const newCertificates = formData.certificates.filter((_, i) => i !== index);
    updateFormData({
      ...formData,
      certificates: newCertificates,
    });

    const newLabels = { ...labels };
    delete newLabels[index];
    setLabels(newLabels);
  };

  const handleLabelChange = (index, value) => {
    setLabels((prev) => ({ ...prev, [index]: value }));
  };

  const handleSaveDocuments = async () => {
    try {
      // Handle profile photo
      let photoURL = formData.photoURL; // Keep existing URL if no new photo
      if (formData.photo instanceof File) {
        const photoRef = storageRef(
          storage,
          `students/${studentId}/profile-photo`,
        );
        await uploadBytes(photoRef, formData.photo);
        photoURL = await getDownloadURL(photoRef);
      }

      // Handle certificates
      const documentUpdates = {};
      documents.forEach((doc, index) => {
        documentUpdates[index] = {
          ...doc,
          label: labels[index] || '',
        };
      });

      await update(ref(db, `students/${studentId}/documents`), {
        photoURL: photoURL,
        certificates: documentUpdates,
      });
      alert('Documents saved successfully!');
    } catch (error) {
      console.error('Error saving documents:', error);
      alert('Failed to save documents.');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Document Upload</h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Photo *
          </label>
          <div className="flex items-center space-x-4">
            {photoPreview && (
              <img
                src={photoPreview}
                alt="Profile Preview"
                className="h-20 w-20 object-cover rounded-full"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required={!formData.photo}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Certificates and Other Documents
          </label>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleCertificateUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />

          {formData.certificates?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700">
                Uploaded Documents:
              </h4>
              {formData.certificates.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md"
                >
                  <div className="flex flex-col">
                    <a
                      href={file.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {(file instanceof File ? file.name : file.originalName) ||
                        `Document ${index + 1}`}
                    </a>
                    {labels[index] && (
                      <span className="text-xs text-gray-500">
                        Label: {labels[index]}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Add a label"
                      value={labels[index] || ''}
                      onChange={(e) => handleLabelChange(index, e.target.value)}
                      className="ml-2 p-1 border rounded"
                    />
                    <button
                      type="button"
                      onClick={() => removeCertificate(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end mt-4">
          <span
            onClick={handleSaveDocuments}
            className="text-blue-600 cursor-pointer"
          >
            Save
          </span>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
